import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  InputAdornment,
} from '@mui/material'
import { IconSearch } from '@tabler/icons-react'
import { Product, PricingType } from '../../types'
import { channels } from '../../../../constants'

interface EditProductDialogProps {
  open: boolean
  product: Product | null
  onClose: () => void
  onUpdate: (data: EditProductFormData) => void
  availableProducts: Product[]
}

export interface EditProductFormData {
  name: string
  bundleId: string
  supplier: string
  price: string
  duration: number
  status: 'active' | 'inactive'
  purchaseConditionType: PricingType
  purchaseConditionValue: string
  selectedProducts: string[]
}

const EditProductDialog = ({
  open,
  product,
  onClose,
  onUpdate,
  availableProducts,
}: EditProductDialogProps) => {
  const [formData, setFormData] = useState<EditProductFormData>({
    name: '',
    bundleId: '',
    supplier: '',
    price: '',
    duration: 30,
    status: 'active',
    purchaseConditionType: 'fixPrice',
    purchaseConditionValue: '',
    selectedProducts: [],
  })
  const [productSearch, setProductSearch] = useState('')

  const isBundle = product?.isBundle === true

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name,
        bundleId: product.displayId?.toString() ?? '',
        supplier: product.supplier ?? '',
        price: product.price,
        duration: product.runtime ?? 30,
        status: product.status,
        purchaseConditionType: product.pricingType,
        purchaseConditionValue: product.condition?.toString() ?? '',
        selectedProducts: product.bundleItems.map((b) => b.id),
      })
    }
  }, [product])

  const handleChange = (field: keyof EditProductFormData, value: string | number | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleClose = () => {
    setProductSearch('')
    onClose()
  }

  const handleUpdate = () => {
    onUpdate(formData)
    setProductSearch('')
  }

  const handleToggleProduct = (productId: string) => {
    const currentSelected = formData.selectedProducts
    const newSelected = currentSelected.includes(productId)
      ? currentSelected.filter((id) => id !== productId)
      : [...currentSelected, productId]
    handleChange('selectedProducts', newSelected)
  }

  const isFormValid = isBundle
    ? formData.name.trim() !== '' &&
      parseFloat(formData.price) > 0 &&
      formData.duration > 0 &&
      parseFloat(formData.purchaseConditionValue) > 0 &&
      formData.selectedProducts.length > 0
    : formData.name.trim() !== '' &&
      formData.supplier !== '' &&
      parseFloat(formData.price) > 0 &&
      formData.duration > 0 &&
      parseFloat(formData.purchaseConditionValue) > 0

  const filteredProducts = availableProducts.filter(
    (p) =>
      p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
      (p.supplier ?? '').toLowerCase().includes(productSearch.toLowerCase())
  )

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h4" color="primary" fontWeight={600}>
          {isBundle ? 'Update Bundle' : 'Update Product'}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
            {isBundle ? 'Bundle Name' : 'Product Name'}
          </Typography>
          <TextField
            fullWidth
            placeholder={isBundle ? 'e.g. Social Media Starter' : 'e.g. Booster'}
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            size="small"
            sx={{ mb: 2 }}
          />

          {!isBundle && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                  Supplier
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={formData.supplier}
                    onChange={(e) => handleChange('supplier', e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select supplier
                    </MenuItem>
                    {channels.map((channel) => (
                      <MenuItem key={channel.value} value={channel.value}>
                        {channel.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                  Sales Price (€)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  placeholder="e.g. 99.99"
                  value={formData.price}
                  onChange={(e) => handleChange('price', e.target.value)}
                  size="small"
                  inputProps={{ min: 0, step: '0.01' }}
                />
              </Grid>
            </Grid>
          )}

          {!isBundle && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                  Duration (Days)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={formData.duration}
                  onChange={(e) => handleChange('duration', parseInt(e.target.value, 10) || 0)}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                  Status
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={formData.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}

          {isBundle && (
            <>
              <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                Contained Products
              </Typography>
              <TextField
                fullWidth
                placeholder="Search products (Name, Supplier)..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                size="small"
                sx={{ mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconSearch size={18} />
                    </InputAdornment>
                  ),
                }}
              />
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px',
                  maxHeight: 200,
                  overflow: 'auto',
                  mb: 2,
                }}
              >
                <List dense disablePadding>
                  {filteredProducts.map((p) => (
                    <ListItem
                      key={p.id}
                      disablePadding
                      secondaryAction={
                        <Typography variant="body2" color="text.secondary">
                          {p.price
                            ? `${parseFloat(p.price).toLocaleString('de-DE', {
                                minimumFractionDigits: 2,
                              })}€`
                            : '—'}
                        </Typography>
                      }
                    >
                      <ListItemButton onClick={() => handleToggleProduct(p.id)} dense>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox
                            edge="start"
                            checked={formData.selectedProducts.includes(p.id)}
                            tabIndex={-1}
                            disableRipple
                            size="small"
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2">
                              <strong>{p.name}</strong>{' '}
                              <Typography component="span" variant="body2" color="text.secondary">
                                {p.supplier ? `(${p.supplier})` : ''} [#{p.displayId}]
                              </Typography>
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            </>
          )}

          {isBundle && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                  Bundle Price (€)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  placeholder="e.g. 99.99"
                  value={formData.price}
                  onChange={(e) => handleChange('price', e.target.value)}
                  size="small"
                  inputProps={{ min: 0, step: '0.01' }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                  Duration (Days)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={formData.duration}
                  onChange={(e) => handleChange('duration', parseInt(e.target.value, 10) || 0)}
                  size="small"
                />
              </Grid>
            </Grid>
          )}

          {isBundle && (
            <>
              <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                Status
              </Typography>
              <FormControl size="small" sx={{ mb: 3, minWidth: 200 }}>
                <Select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </>
          )}

          <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 2 }}>
            Purchase Conditions
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Type
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={formData.purchaseConditionType}
                  onChange={(e) => handleChange('purchaseConditionType', e.target.value)}
                >
                  <MenuItem value="fixPrice">Fix Price</MenuItem>
                  <MenuItem value="percentage">Percentage</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Conditions
              </Typography>
              <TextField
                fullWidth
                type="number"
                placeholder={formData.purchaseConditionType === 'fixPrice' ? '100.00' : '15'}
                value={formData.purchaseConditionValue}
                onChange={(e) => handleChange('purchaseConditionValue', e.target.value)}
                size="small"
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Discount on the sales price
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{ px: 4, borderRadius: '8px', textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpdate}
          disabled={!isFormValid}
          sx={{ px: 4, borderRadius: '8px', textTransform: 'none' }}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditProductDialog
